import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { getErrorMessage } from '../shared/utils/errorMessage';
import { getNetworkOnline } from '../shared/utils/networkStatus';
// Safe to import: fcm-service imports notification-service type-only, so there is no
// runtime cycle back to this module. Importing notificationService here would create one.
import fcmService from './fcm-service';

/** Axios request config plus per-call error-handling options. */
export type ApiRequestConfig = AxiosRequestConfig & { skipErrorToast?: boolean };

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';
const NETWORK_ERROR_MESSAGE = 'Network error. Please check your internet connection and try again.';

/**
 * Decide what the global interceptor should do with a failed request.
 * Pure and exported for testing — no side effects.
 */
export function buildErrorToast(error: unknown): { message: string | null; sessionExpired: boolean } {
  const e = error as { config?: { skipErrorToast?: boolean }; response?: { status?: number } };

  if (e?.config?.skipErrorToast) {
    return { message: null, sessionExpired: false };
  }

  if (e?.response?.status === 401) {
    return { message: SESSION_EXPIRED_MESSAGE, sessionExpired: true };
  }

  // Transport-level failure (no HTTP response): network down, DNS, CORS, timeout.
  // When we already know we're offline, the OfflineOverlay communicates it — don't
  // double up with a toast. Otherwise show the friendly connectivity message.
  if (!e?.response) {
    return { message: getNetworkOnline() ? NETWORK_ERROR_MESSAGE : null, sessionExpired: false };
  }

  return { message: getErrorMessage(error), sessionExpired: false };
}

/**
 * API response interface
 */
export interface ApiResponse<T = unknown> {
  data: T;
  status: string;
  message?: string;
  error?: string;
  validationErrors?: Record<string, string>;
}

/**
 * API Client - Singleton service for handling API requests
 */
class ApiClient {
  private static instance: ApiClient;
  private client: AxiosInstance;

  private constructor() {
    const baseURL = process.env.REACT_APP_API_URL;

    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      timeout: 30000
    });

    this.setupInterceptors();
  }

  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  private setupInterceptors(): void {
    // Request interceptor - Add auth token
    this.client.interceptors.request.use(config => {
      const token = localStorage.getItem('token');

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    });

    // Response interceptor - single source of error toasts
    this.client.interceptors.response.use(
      response => response,
      error => {
        const { message, sessionExpired } = buildErrorToast(error);

        if (sessionExpired) {
          localStorage.removeItem('token');

          // This is the second way out of a session, and it bypasses React entirely —
          // AuthContext.logout() never runs here. Tear push down anyway, or the device
          // keeps receiving notifications for a session it no longer has.
          //
          // No backend unregister: the JWT is already dead, so that call would 401 too.
          // deleteToken() is what closes the loop — the backend's next send to this
          // token comes back UNREGISTERED and the stale row is pruned server-side.
          void fcmService
            .teardown()
            .catch(() => undefined)
            .finally(() => {
              // Redirect regardless of whether teardown succeeded or hung.
              window.location.href = '/login';
            });
        }

        if (message) {
          toast.error(message);
        }

        return Promise.reject(error);
      }
    );
  }

  public async get<T = unknown>(url: string, config?: ApiRequestConfig): Promise<T> {
    const response = await this.client.get<ApiResponse<T>>(url, config);
    return response.data.data;
  }

  public async post<T = unknown>(url: string, data?: Record<string, unknown> | FormData | object, config?: ApiRequestConfig): Promise<T> {
    const response = await this.client.post<ApiResponse<T>>(url, data, config);
    return response.data.data;
  }

  public async postFormData<T = unknown>(url: string, formData: FormData, config?: ApiRequestConfig): Promise<T> {
    const formConfig = {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    };
    const response = await this.client.post<ApiResponse<T>>(url, formData, formConfig);
    return response.data.data;
  }

  public async put<T = unknown>(url: string, data?: Record<string, unknown> | FormData | object, config?: ApiRequestConfig): Promise<T> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config);
    return response.data.data;
  }

  public async patch<T = unknown>(url: string, data?: Record<string, unknown> | FormData | object, config?: ApiRequestConfig): Promise<T> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config);
    return response.data.data;
  }

  public async delete<T = unknown>(url: string, config?: ApiRequestConfig): Promise<T> {
    const response = await this.client.delete<ApiResponse<T>>(url, config);
    return response.data.data;
  }

  public getBaseUrl(): string {
    return this.client.defaults.baseURL || '';
  }
}

// Export singleton instance
const apiClient = ApiClient.getInstance();
export default apiClient;
